import React, { useState, useEffect } from "react";
import { Bell, Check, Send } from "lucide-react";
import api from "../api";
import { K } from "../theme";
import { Cd, Sec, Inp, Bt } from "../components";

export const ReminderConfigCard = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get('/api/push/reminder-config')
      .then(setCfg)
      .catch(() => setCfg({ enabled: false, time: "09:35", working_days: [1, 2, 3, 4, 5], message: "" }));
  }, []);

  if (!cfg) return null;

  const toggleDay = (d) => {
    const wd = cfg.working_days.includes(d)
      ? cfg.working_days.filter(x => x !== d)
      : [...cfg.working_days, d].sort();
    setCfg({ ...cfg, working_days: wd });
  };

  const save = async () => {
    setSaving(true);
    try {
      const d = await api.put('/api/push/reminder-config', cfg);
      setCfg(d);
      setMsg("Saved");
    } catch (e) {
      setMsg("Error: " + (e.message || "Failed"));
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(""), 3000);
    }
  };

  const dispatch = async () => {
    try {
      const r = await api.post('/api/push/dispatch', {});
      setMsg(`Dispatched: ${r.sent}/${r.candidates} sent`);
    } catch (e) {
      setMsg("Error: " + (e.message || "Failed"));
    } finally {
      setTimeout(() => setMsg(""), 5000);
    }
  };

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <Cd style={{ marginBottom: 14, borderLeft: `3px solid ${cfg.enabled ? K.bl : K.t3}` }}>
      <div className="flex items-center justify-between mb-[10px]">
        <Sec icon={Bell} title="Daily Check-In Reminder (Web Push)" />
        <label className="flex items-center gap-[6px] text-[14px] text-t1 cursor-pointer">
          <input type="checkbox" checked={!!cfg.enabled} onChange={e => setCfg({ ...cfg, enabled: e.target.checked })} />
          <span>{cfg.enabled ? "Enabled" : "Disabled"}</span>
        </label>
      </div>
      <div className="text-[14px] text-t2 mb-[10px]">Pushes a reminder to active employees who haven't checked in yet (skips those on approved leave). Time is IST.</div>
      <div className="grid grid-cols-[180px_1fr] gap-[10px] mb-[10px]">
        <div>
          <div className="text-[12px] text-t3 mb-[3px]">Reminder Time (HH:MM)</div>
          <Inp type="time" value={cfg.time} onChange={v => setCfg({ ...cfg, time: v })} />
        </div>
        <div>
          <div className="text-[12px] text-t3 mb-[3px]">Working Days</div>
          <div className="flex gap-1">
            {dayNames.map((n, i) => {
              const on = cfg.working_days.includes(i);
              return (
                <button
                  key={i}
                  onClick={() => toggleDay(i)}
                  className={`px-[10px] py-[7px] rounded-lg border text-[14px] font-semibold cursor-pointer ${on ? "border-p bg-pb text-p" : "border-b1 bg-bg2 text-t3"}`}
                >{n}</button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="mb-[10px]">
        <div className="text-[12px] text-t3 mb-[3px]">Message (max 200 chars)</div>
        <Inp value={cfg.message || ""} onChange={v => setCfg({ ...cfg, message: v.slice(0, 200) })} placeholder="You haven't checked in yet today. Tap to check in now." />
      </div>
      <div className="flex gap-[6px] items-center">
        <Bt s="sm" icon={Check} disabled={saving} onClick={save}>{saving ? "Saving..." : "Save Reminder Config"}</Bt>
        <Bt v="secondary" s="sm" icon={Send} onClick={dispatch}>Dispatch Now (test)</Bt>
        {msg && <span className={`text-[14px] ml-[6px] ${msg.startsWith("Error") ? "text-r" : "text-g"}`}>{msg}</span>}
      </div>
    </Cd>
  );
};
